import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { iconBtn } from './redesign';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Dialog heading; also used as the accessible name. */
  title?: ReactNode;
  /** Accessible label for the close button. */
  closeLabel?: string;
  /** Shown above the title (e.g. the round icon of the share dialog). */
  leading?: ReactNode;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Bottom sheet on phones, centered dialog on desktop (prototype .overlay/.sheet).
 * Backdrop click and Escape close it; focus stays inside while open and returns to the
 * element that opened it.
 */
export default function Sheet({
  open,
  onClose,
  title,
  closeLabel = 'Close',
  leading,
  children,
}: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      opener?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200]">
      <div
        className="absolute inset-0 bg-[rgba(30,28,25,0.5)]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute inset-0 flex items-end justify-center desk:items-center desk:p-4">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className="pointer-events-auto relative max-h-[92%] w-full overflow-auto rounded-t-[22px] bg-white p-6 pb-[calc(24px+env(safe-area-inset-bottom,0px))] font-text text-ink shadow-[0_40px_90px_-30px_rgba(0,0,0,0.55)] animate-sheet-up motion-reduce:animate-none outline-none desk:max-h-[88%] desk:w-[min(460px,100%)] desk:rounded-[22px] desk:pb-6"
        >
          {/* First in the DOM so it takes the initial focus (not a text field). */}
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className={`${iconBtn} absolute right-2.5 top-2.5`}
          >
            <X className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
          {leading}
          {title && (
            <h3 id={titleId} className="pr-[30px] font-ui text-[19px] font-bold leading-[1.6]">
              {title}
            </h3>
          )}
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
